import React, { useCallback, useEffect, useRef, useState } from 'react';
import { ChevronLeftIcon, ChevronRightIcon } from 'lucide-react';
import { insidePages } from '../data/insidePages';
import { OFFER_ANCHOR, PRODUCT } from '../data/constants';
export function LookInside() {
  const trackRef = useRef<HTMLDivElement | null>(null);
  const [active, setActive] = useState(0);
  const scrollTo = useCallback((index: number) => {
    const track = trackRef.current;
    if (!track) return;
    const clamped = Math.max(0, Math.min(index, insidePages.length - 1));
    track.scrollTo({
      left: clamped * track.clientWidth,
      behavior: 'smooth'
    });
    setActive(clamped);
  }, []);
  useEffect(() => {
    const track = trackRef.current;
    if (!track) return;
    const onScroll = () => {
      const next = Math.round(track.scrollLeft / track.clientWidth);
      setActive(Math.max(0, Math.min(next, insidePages.length - 1)));
    };
    track.addEventListener('scroll', onScroll, {
      passive: true
    });
    return () => track.removeEventListener('scroll', onScroll);
  }, []);
  const arrowClass = 'absolute top-1/2 z-10 -translate-y-1/2 rounded-full border-2 border-forest bg-gold p-2 text-forest shadow-lg transition-[background-color,opacity] duration-150 ease-out hover:bg-[#E0B43E] disabled:pointer-events-none disabled:opacity-0 sm:p-3';
  return <section className="border-y-4 border-gold bg-white py-5 sm:py-7" data-id="element-86" data-mp="f2sogqo1nebu">
      <div className="mx-auto max-w-5xl px-4 sm:px-6" data-id="element-87" data-mp="3nhaygxxjp3y3">
        <h2 className="text-center text-3xl leading-none text-forest sm:text-5xl" data-id="element-88" data-mp="3nhu5hjlnq7xc">
          Look <span className="text-leaf" data-id="element-89" data-mp="3ik2hv1s1p7yy">inside</span>
        </h2>
        <p className="mt-1 text-center font-display text-sm font-semibold uppercase leading-tight text-forest sm:text-lg" data-id="element-90" data-mp="12hrr701z5ir">
          See {PRODUCT} In Action
        </p>
        <p className="mx-auto mt-1 max-w-3xl text-center text-sm leading-snug text-ink sm:text-base" data-id="element-91" data-mp="3seugm8xd60vb">
          Explore annotated deficiency photographs, side-by-side leaf comparisons, nutrient
          interaction charts, soil and tissue interpretation guides, crop-specific visual
          references, and ready-to-use professional assessment resources.
        </p>

        <div className="relative mt-3" data-id="element-92" data-mp="18m3p83m251c8">
          <div ref={trackRef} className="no-scrollbar flex snap-x snap-mandatory overflow-x-auto overscroll-x-contain" aria-label="Interior pages carousel" data-id="element-93" data-mp="1dewuqtpcodpd">
            {insidePages.map((page, i) => <div key={page.src} className="w-full shrink-0 snap-center px-0.5" role="group" aria-roledescription="slide" aria-label={`Page ${i + 1} of ${insidePages.length}`} data-id="element-94" data-mp="krk86jj6w5w2" data-mpi={i}>
                <a href={OFFER_ANCHOR} className="mx-auto block max-w-3xl overflow-hidden rounded-lg border-2 border-forest bg-white shadow-xl" data-id="element-95" data-mp="yq0lwufgywnp">
                  <img src={page.src} alt={page.alt} loading={i === 0 ? 'eager' : 'lazy'} className="block w-full object-contain" data-id="element-96" data-mp="2a44ebw6escqn" />
                </a>
              </div>)}
          </div>

          <button type="button" onClick={() => scrollTo(active - 1)} disabled={active === 0} aria-label="Previous page" className={`${arrowClass} left-1 sm:left-4`} data-id="element-97" data-mp="2ai9iggtyqgd0">
            <ChevronLeftIcon className="h-5 w-5" strokeWidth={3} data-id="element-98" data-mp="21jo1ur81xbev" />
          </button>
          <button type="button" onClick={() => scrollTo(active + 1)} disabled={active === insidePages.length - 1} aria-label="Next page" className={`${arrowClass} right-1 sm:right-4`} data-id="element-99" data-mp="1whowxcwny6ft">
            <ChevronRightIcon className="h-5 w-5" strokeWidth={3} data-id="element-100" data-mp="3lypz2dh9731g" />
          </button>
        </div>

        <div className="mt-2.5 flex items-center justify-center gap-2" data-id="element-101" data-mp="3hw33q2ldizzi">
          {insidePages.map((page, i) => <button key={page.src} type="button" onClick={() => scrollTo(i)} aria-label={`Go to page ${i + 1}`} aria-current={i === active} className={`h-2 rounded-full transition-[width,background-color] duration-150 ease-out ${i === active ? 'w-7 bg-leaf' : 'w-2 bg-forest/25 hover:bg-forest/50'}`} data-id="element-102" data-mp="3p0ugogyukz5q" data-mpi={i} />)}
        </div>
      </div>
    </section>;
}